/**
 * SwarmTopControls — a theme-agnostic header control for any swarm page.
 * Renders a "Dashboard" back button and a "Projects" popover that lets the user
 * create a new project for THIS swarm or jump to the Projects hub — so projects
 * are reachable from inside every swarm, not just the dashboard.
 */
import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, FolderKanban, Plus, Loader2, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SwarmTopControls({ swarm, accent = "#2DD4A8", panel = "#0F1830", border = "#1E2D4A", showBack = true, showProjects = true }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openMenu = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
    }
    setOpen((v) => !v);
  };

  const btn = {
    display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer",
    background: "rgba(255,255,255,0.04)", border: `1px solid ${border}`, borderRadius: 10,
    padding: "8px 12px", color: "#DCE3F0", fontSize: 13, fontWeight: 500,
  };

  const createProject = async () => {
    if (!name.trim()) { toast.error("Enter a project name"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/projects`, { swarm, name: name.trim() });
      toast.success("Project created");
      setName(""); setOpen(false);
      navigate("/dashboard?tab=projects");
    } catch (e) { toast.error("Could not create project"); }
    setSaving(false);
  };

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <button onClick={() => navigate("/dashboard")} style={{ ...btn, display: showBack ? "inline-flex" : "none" }} data-testid="swarm-back-dashboard" title="Back to dashboard">
        <ArrowLeft size={15} /> Dashboard
      </button>

      <div style={{ position: "relative", display: showProjects ? "inline-flex" : "none" }}>
        <button ref={btnRef} onClick={openMenu} style={{ ...btn, color: accent, borderColor: `${accent}55` }}
          data-testid="swarm-projects-btn" title="Projects">
          <FolderKanban size={15} /> Projects
        </button>

        {open && ReactDOM.createPortal(
          <div ref={ref} data-testid="swarm-projects-menu" onClick={(e) => e.stopPropagation()}
            style={{ position: "fixed", top: pos.top, right: pos.right, width: 268, zIndex: 9999,
              background: panel, border: `1px solid ${border}`, borderRadius: 14,
              boxShadow: "0 14px 44px rgba(0,0,0,0.5)", padding: 12 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8593AC", fontWeight: 600 }}>New project</span>
              <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#8593AC", display: "flex" }}><X size={14} /></button>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") createProject(); }}
                placeholder="Project name…" data-testid="swarm-new-project-name"
                style={{ flex: 1, background: "#0A1120", border: `1px solid ${border}`, borderRadius: 9, padding: "8px 10px", fontSize: 13, color: "#fff", outline: "none" }} />
              <button onClick={createProject} disabled={saving || !name.trim()} data-testid="swarm-new-project-create"
                style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, background: accent, border: "none", borderRadius: 9, cursor: "pointer", opacity: saving || !name.trim() ? 0.5 : 1, color: "#04211A" }}>
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
              </button>
            </div>
            <button onClick={() => navigate("/dashboard?tab=projects")} data-testid="swarm-open-projects-hub"
              style={{ marginTop: 10, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "none", border: `1px solid ${border}`, borderRadius: 9, padding: "8px 10px", cursor: "pointer", color: "#DCE3F0", fontSize: 13 }}>
              <ExternalLink size={14} /> Open Projects hub
            </button>
          </div>, document.body)}
      </div>
    </div>
  );
}
